import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { post } from "@/shared/utils/fetchApi";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import { CheckCircle2, XCircle, LogIn } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";

type LinkStatus = "loading" | "needs_auth" | "confirming" | "success" | "error";

export default function CommunicatorLogin() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const [status, setStatus] = useState<LinkStatus>("loading");
  const [errorMessage, setErrorMessage] = useState("");

  const confirmLink = useCallback(async () => {
    if (!token) {
      setStatus("error");
      setErrorMessage(t("communicator.login.missingToken"));
      return;
    }

    setStatus("confirming");

    try {
      await post<{ message: string }>("/communicator/link/confirm", {
        token,
      });

      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMessage(
        err instanceof Error
          ? err.message
          : t("communicator.login.confirmFailed"),
      );
    }
  }, [token, t]);

  useEffect(() => {
    if (authLoading) return;

    if (!token) {
      setStatus("error");
      setErrorMessage(t("communicator.login.missingToken"));
      return;
    }

    if (!user) {
      // Save current URL so after login we come back here
      sessionStorage.setItem(
        "prodgy_redirect_after_login",
        `/communicator/login?token=${token}`,
      );
      setStatus("needs_auth");
      return;
    }

    // User is authenticated — confirm the link
    confirmLink();
  }, [user, authLoading, token, confirmLink, t]);

  if (authLoading || status === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner className="h-12 w-12" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
      <div className="w-full max-w-md space-y-4 animate-fade-in">
        <div className="flex justify-center">
          <img
            src="/assets/logo.png"
            alt="Prodgy Logo"
            className="h-32 w-auto mb-2"
          />
        </div>

        <Card className="w-full shadow-lg">
          <CardContent className="pt-6">
            <div className="space-y-4 text-center">
              <p className="text-sm text-muted-foreground">
                {t("communicator.login.subtitle")}
              </p>

              {/* Needs Auth */}
              {status === "needs_auth" && (
                <div className="space-y-4 pt-2">
                  <LogIn className="h-12 w-12 mx-auto text-primary" />
                  <h2 className="text-lg font-medium tracking-tight">
                    {t("communicator.login.authRequired")}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {t("communicator.login.authRequiredDescription")}
                  </p>
                  <Button
                    onClick={() => navigate("/login")}
                    className="w-full font-normal h-11"
                  >
                    {t("communicator.login.goToLogin")}
                  </Button>
                </div>
              )}

              {/* Confirming */}
              {status === "confirming" && (
                <div className="space-y-4 pt-2">
                  <LoadingSpinner className="h-12 w-12 mx-auto" />
                  <p className="text-sm text-muted-foreground">
                    {t("communicator.login.confirming")}
                  </p>
                </div>
              )}

              {/* Success */}
              {status === "success" && (
                <div className="space-y-4 pt-2">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-green-600" />
                  <h2 className="text-lg font-medium tracking-tight text-green-600">
                    {t("communicator.login.success")}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {t("communicator.login.successDescription")}
                  </p>
                  <p className="text-xs text-muted-foreground pt-4">
                    {t("communicator.login.canCloseTab")}
                  </p>
                </div>
              )}

              {/* Error */}
              {status === "error" && (
                <div className="space-y-4 pt-2">
                  <XCircle className="h-12 w-12 mx-auto text-destructive" />
                  <h2 className="text-lg font-medium tracking-tight text-destructive">
                    {t("communicator.login.errorTitle")}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {errorMessage}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="w-full font-normal h-11"
                  >
                    {t("communicator.login.goHome")}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Powered by footer */}
      <div className="absolute bottom-4 right-4">
        <span className="text-sm text-muted-foreground opacity-80">
          {t("common.poweredBy")}{" "}
          <a
            href="https://programmers.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-muted-foreground/80 transition-colors"
          >
            {t("common.companyName")}
          </a>
          {t("common.trademark")}
        </span>
      </div>
    </div>
  );
}
